import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import { ArticleCard } from '../../components/blog/article-card';
import { Button } from '../../components/ui/button';
import { useTopics } from '../../hooks/use-topics';
import { useArticlesByTopic } from '../../hooks/use-articles';

export default function TopicPage() {
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState('latest');
  const [activeTimeFilter, setActiveTimeFilter] = useState('all-time');

  // Use hooks correctly
  const { getAllTopics, getTopicBySlug } = useTopics(slug);

  // Get topic data - getTopicBySlug is an object with data, isLoading properties
  const { data: topic, isLoading: isLoadingTopic } = getTopicBySlug;

  // Use the hook to get articles by topic
  const { articles, isLoading: isArticlesLoading } = useArticlesByTopic(slug);

  // Sort articles by date (newest first)
  const sortedArticles = [...(articles || [])].sort((a, b) => {
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return (
    <div className="flex min-h-screen flex-col bg-black text-white font-sans selection:bg-violet-500/30 relative">
      <Navbar />

      {/* Gradient Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      <main className="flex-1 relative z-10">
        <div className="container mx-auto px-4 py-12 md:px-6">
          {isLoadingTopic ? (
            <div className="animate-pulse mb-12">
              <div className="h-12 bg-zinc-800 rounded-lg w-1/3 mb-4"></div>
              <div className="h-6 bg-zinc-800 rounded-lg w-2/3"></div>
            </div>
          ) : (
            <div className="mb-12 text-center md:text-left">
              <div className="inline-block p-3 bg-gradient-to-r from-violet-500/10 to-cyan-500/10 rounded-2xl mb-4 border border-violet-500/20">
                <h1 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-violet-200 to-cyan-200 tracking-tight">
                  {topic?.name || slug}
                </h1>
              </div>
              <p className="text-lg text-zinc-400 max-w-2xl">
                {topic?.description ||
                  'Explore the latest articles, insights, and discussions related to this topic.'}
              </p>
            </div>
          )}

          {isArticlesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="rounded-3xl border border-white/5 bg-zinc-900/50 overflow-hidden h-[400px]"
                >
                  <div className="h-48 bg-zinc-800/50 animate-pulse" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-zinc-800/50 rounded animate-pulse w-1/3" />
                    <div className="h-8 bg-zinc-800/50 rounded animate-pulse w-3/4" />
                    <div className="h-4 bg-zinc-800/50 rounded animate-pulse w-full" />
                    <div className="h-4 bg-zinc-800/50 rounded animate-pulse w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedArticles.map((article, index) => (
                <div
                  key={article.id}
                  className="group relative"
                  style={{
                    animationDelay: `${index * 100}ms`,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 to-cyan-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                  <div className="relative bg-zinc-900/40 backdrop-blur-sm border border-white/5 rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-500 hover:scale-[1.02] hover:bg-zinc-900/60 hover:border-violet-500/20 h-full">
                    <ArticleCard article={article} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 border border-white/5 rounded-3xl bg-black/40 backdrop-blur-md">
              <div className="w-20 h-20 bg-zinc-900/50 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-zinc-500"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">
                No articles found
              </h3>
              <p className="text-zinc-400 mb-8 max-w-md mx-auto">
                There are no articles in this topic yet. Be the first to
                contribute or explore other topics.
              </p>
              <Button className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-bold shadow-lg shadow-violet-500/20 rounded-xl px-8 py-6 h-auto text-lg">
                Explore Other Topics
              </Button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
